import React from 'react'

import { Button, Dropdown, Menu, Popover, Tag } from 'antd'
import { ColumnFilterItem } from 'antd/lib/table/interface'

import { MoreOutlined } from '@ant-design/icons'

import { emptyCharacter } from '../../../components/EmptyChar'
import { renderEmail } from '../../../components/tables/table-column-renderers'
import {
  buildSearchFilterProps,
  sortDateColumn,
  sortNumericColumn,
  sortStringColumn,
} from '../../../components/tables/utils'
import { momentDateHandler } from '../../../date/moment-date-handler'
import { loginStatuses, User, UserTableItem } from '../models'

const { newDate, isAfter, addDays, now, startOfDay, formatDateTime } = momentDateHandler

export const renderUserStatusTag = () => (status: string) =>
  status ? (
    <Tag color={status === 'enabled' ? 'green' : 'red'} className="capitalize">
      {status}
    </Tag>
  ) : (
    emptyCharacter
  )

export const render2FAStatus = () => (isTwoFactorAuthEnabled: boolean) =>
  (
    <Tag color={isTwoFactorAuthEnabled ? 'green' : 'default'} className="capitalize">
      {isTwoFactorAuthEnabled ? 'Enabled' : 'Disabled'}
    </Tag>
  )

export const renderGlobalRoleTag = () => (globalRole: string) =>
  globalRole ? (
    <Tag color="green" className="capitalize">
      {globalRole}
    </Tag>
  ) : (
    emptyCharacter
  )

const renderPasswordExpires = (passwordExpiresAt: Date) =>
  isAfter({ firstDate: newDate(passwordExpiresAt), secondDate: now() }) ? (
    formatDateTime({ date: newDate(passwordExpiresAt) })
  ) : (
    <Tag color="red">Expired</Tag>
  )

const renderLoginStatusTag = () => (loginStatus: string) =>
  (
    <Tag color={loginStatuses[loginStatus].color} className="capitalize">
      {loginStatuses[loginStatus].label}
    </Tag>
  )

const renderActionsMenu =
  ({
    onUpdate,
    onExtendPassword,
    onResendPassword,
  }: {
    onUpdate?: (issuer: User) => void
    onExtendPassword: (user: User) => void
    onResendPassword: (user: User) => void
  }) =>
  (user: User) => {
    const sevenDaysFromNow = startOfDay(addDays({ days: 7 }))
    const passwordExpirationDate = startOfDay(newDate(new Date(user.passwordExpiresAt)))
    const passwordExpiresInMoreThanSevenDays = isAfter({
      firstDate: passwordExpirationDate,
      secondDate: sevenDaysFromNow,
    })

    const menu = (
      <Menu>
        <Menu.Item key="onUpdate" onClick={() => onUpdate(user)}>
          Update User
        </Menu.Item>
        <Menu.Item
          key="onExtendPassword"
          onClick={() => onExtendPassword(user)}
          disabled={passwordExpiresInMoreThanSevenDays}
        >
          Extend Password Expiry
        </Menu.Item>
        <Menu.Item key="onUpdate" onClick={() => onResendPassword(user)}>
          Re-send temporary password
        </Menu.Item>
      </Menu>
    )

    return (
      <Dropdown placement="bottomRight" overlay={menu}>
        <Button icon={<MoreOutlined />} />
      </Dropdown>
    )
  }

const renderUserDomain = (_: any, user: UserTableItem) => {
  const { tenants, globalRole } = user
  const isImperiumGlobalUser = !!globalRole

  if (isImperiumGlobalUser || !tenants || tenants.length === 0) return emptyCharacter

  const tenantsSortedByPaying = tenants.sort((t) => (t.isDemo || t.isTrial ? 1 : -1))

  const [firstTenant, ...otherTenants] = tenantsSortedByPaying

  if (otherTenants.length === 0) return firstTenant.domain

  const tenantList = (
    <ul>
      {tenantsSortedByPaying.map((t) => (
        <li>{t.domain}</li>
      ))}
    </ul>
  )

  return (
    <React.Fragment>
      {firstTenant.domain}
      <Popover content={tenantList} title="Tenants" trigger="hover">
        <Tag className="tenant-hover">{`(${otherTenants.length} more)`}</Tag>
      </Popover>
    </React.Fragment>
  )
}

export const buildUsersTableColumns = ({
  onUpdate,
  onExtendPassword,
  onResendPassword,
  filters,
}: {
  onUpdate: (user: User) => void
  onExtendPassword: (user: User) => void
  onResendPassword: (user: User) => void
  filters: {
    globalRoles: ColumnFilterItem[]
  }
}) => [
  {
    title: 'Id',
    dataIndex: 'id',
    sorter: sortNumericColumn({ dataIndex: 'id' }),
    ...buildSearchFilterProps({ dataIndex: 'id' }),
  },
  {
    title: 'Name',
    dataIndex: 'name',
    sorter: sortStringColumn({ dataIndex: 'name' }),
    ...buildSearchFilterProps({ dataIndex: 'name' }),
  },
  {
    title: 'Email',
    dataIndex: 'email',
    sorter: sortStringColumn({ dataIndex: 'email' }),
    ...buildSearchFilterProps({ dataIndex: 'email', render: renderEmail }),
  },
  {
    title: 'Tenant',
    dataIndex: 'domain',
    ...buildSearchFilterProps({ dataIndex: 'tenantNames', render: renderUserDomain }),
  },
  {
    title: 'Status',
    dataIndex: 'userStatus',
    sorter: sortStringColumn({ dataIndex: 'userStatus' }),
    render: renderUserStatusTag(),
  },
  {
    title: 'Global Role',
    dataIndex: 'globalRole',
    render: renderGlobalRoleTag(),
    filters: filters.globalRoles.filter(({ text }) => String(text).toLowerCase() !== 'bank'),
    onFilter: (globalRole: string, user: User) => user.globalRole === globalRole,
  },
  {
    title: 'Login status',
    dataIndex: 'loginStatus',
    sorter: sortStringColumn({ dataIndex: 'loginStatus' }),
    render: renderLoginStatusTag(),
  },
  {
    title: 'Last Login',
    dataIndex: 'lastLoginAt',
    sorter: sortDateColumn({ dataIndex: 'lastLoginAt' }),
    render: (lastLoginAt: Date) => formatDateTime({ date: newDate(lastLoginAt) }),
  },
  {
    title: 'Password Expires',
    dataIndex: 'passwordExpiresAt',
    sorter: sortDateColumn({ dataIndex: 'passwordExpiresAt' }),
    render: renderPasswordExpires,
  },
  {
    title: '2FA',
    dataIndex: 'twoFactorAuthEnabled',
    sorter: sortStringColumn({ dataIndex: 'twoFactorAuthEnabled' }),
    render: render2FAStatus(),
  },
  { title: '', render: renderActionsMenu({ onUpdate, onExtendPassword, onResendPassword }) },
]
